import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Mail, MailOpen, Plus, X, Eye, User, Shield } from "lucide-react";
import webIconSendSvg from '@/assets/web_icon_send.svg';
import type { SupportMessage } from "@mangelapp/shared";

interface ThreadedMessage {
  original: SupportMessage;
  replies: SupportMessage[];
}

interface SupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embedded?: boolean;
}

export function SupportDialog({ open, onOpenChange, embedded = false }: SupportDialogProps) {
  const { t, i18n } = useTranslation(['components.support']);
  const { formatDateTime } = useDateFormat();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showNewMessageForm, setShowNewMessageForm] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState<SupportMessage | null>(null);
  const [showMessageView, setShowMessageView] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Fetch support messages with background polling every 15 seconds
  const { data: messagesResponse, isLoading } = useQuery({
    queryKey: ["/api/support/messages"],
    queryFn: async () => {
      const response = await apiRequest("/api/support/messages");
      return await response.json();
    },
    enabled: open || embedded,
    staleTime: 10 * 1000, // 10 seconds - allow refetch interval to work
    refetchInterval: 15 * 1000, // Poll every 15 seconds for new messages
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const messages = messagesResponse?.data || [];

  // Group messages into threads (original messages with their replies)
  const threads: ThreadedMessage[] = useMemo(() => {
    const originalMessages = messages.filter((msg: SupportMessage) => !msg.parentMessageId);
    const replies = messages.filter((msg: SupportMessage) => msg.parentMessageId);

    return originalMessages.map((original: SupportMessage) => ({
      original,
      replies: replies.filter((reply: SupportMessage) => reply.parentMessageId === original.id)
        .sort((a: SupportMessage, b: SupportMessage) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        ),
    })).sort((a: ThreadedMessage, b: ThreadedMessage) =>
      new Date(b.original.createdAt).getTime() - new Date(a.original.createdAt).getTime()
    );
  }, [messages]);

  // Create new message with optimistic update
  const createMessageMutation = useMutation({
    mutationFn: async (data: { subject: string; message: string }) => {
      return apiRequest("/api/support/messages", {
        method: "POST",
        body: JSON.stringify(data)
      });
    },
    onMutate: async (newMessage) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/support/messages"] });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(["/api/support/messages"]);

      // Optimistically add the new message
      const optimisticMessage: SupportMessage = {
        id: `temp-${Date.now()}`,
        userId: '',
        ticketId: '',
        subject: newMessage.subject,
        message: newMessage.message,
        folder: 'inbox',
        isFromUser: true,
        isRead: true,
        isPinned: false,
        isMarked: false,
        parentMessageId: null,
        adminResponse: null,
        adminId: null,
        adminActorType: null,
        adminActorId: null,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      queryClient.setQueryData(["/api/support/messages"], (old: any) => ({
        ...old,
        data: [...(old?.data || []), optimisticMessage],
      }));

      // Return context with previous value for rollback
      return { previousMessages };
    },
    onSuccess: () => {
      toast({
        title: t('components.support:toastSentTitle'),
        description: t('components.support:toastSentDesc'),
      });
      setSubject("");
      setMessage("");
    },
    onError: (_error, _newMessage, context) => {
      // Rollback on error
      if (context?.previousMessages) {
        queryClient.setQueryData(["/api/support/messages"], context.previousMessages);
      }
      toast({
        title: t('components.support:toastErrorTitle'),
        description: t('components.support:toastErrorDesc'),
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Always refetch after mutation settles to get server data
      queryClient.invalidateQueries({ queryKey: ["/api/support/messages"] });
    }
  });

  // Mark as read
  const markAsReadMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return apiRequest(`/api/support/messages/${messageId}/read`, {
        method: "PUT"
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/support/messages"] });
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast({
        title: t('components.support:toastErrorTitle'),
        description: t('components.support:validationRequired'),
        variant: "destructive",
      });
      return;
    }
    createMessageMutation.mutate({ subject, message });
  };

  const getLocaleTag = () => {
    const lang = i18n.language || 'en';
    if (lang.startsWith('de')) return 'de-DE';
    if (lang.startsWith('tr')) return 'tr-TR';
    return 'en-US';
  };

  // Flatten all messages chronologically for WhatsApp-style chat
  const allMessages = useMemo(() => {
    return [...messages].sort((a: SupportMessage, b: SupportMessage) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [messages]);

  // Auto-scroll to bottom when messages change - scroll container directly to avoid page scroll
  useEffect(() => {
    if (embedded && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [allMessages, embedded]);

  // Mark unread admin messages as read when chat opens
  useEffect(() => {
    if (embedded && allMessages.length > 0) {
      const unreadMessages = allMessages.filter((m: SupportMessage) => !m.isRead && !m.isFromUser);
      unreadMessages.forEach((msg: SupportMessage) => {
        markAsReadMutation.mutate(msg.id);
      });
    }
  }, [embedded, allMessages.length]);

  // Format time for chat bubbles
  const formatTimeShort = (dateInput: string | Date | number) => {
    return formatDateTime(dateInput instanceof Date ? dateInput : new Date(dateInput));
  };

  // Handle chat message submit (no subject required)
  const handleChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    createMessageMutation.mutate({
      subject: 'Support Chat',
      message: message.trim()
    });
  };

  // WhatsApp-style embedded chat layout
  if (embedded) {
    return (
      <div className="flex-1 flex flex-col border border-black rounded-[18px] bg-white min-h-0 overflow-hidden">
        {/* Header */}
        <div className="p-3 border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-slate-900">{t('components.support:embeddedHeader')}</span>
          </div>
        </div>

        {/* Messages area - WhatsApp style */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50 min-h-0">
          {isLoading ? (
            <div className="text-center text-xs text-slate-500 py-4">
              {t('components.support:embeddedLoading')}
            </div>
          ) : allMessages.length === 0 ? (
            <div className="text-center text-xs text-slate-500 py-4">
              {t('components.support:embeddedEmpty')}
            </div>
          ) : (
            <>
              {allMessages.map((msg: SupportMessage) => {
                const isUser = msg.isFromUser;
                return (
                  <div
                    key={msg.id}
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-2`}
                  >
                    <div
                      className={`max-w-[75%] p-3 rounded-xl border relative ${isUser
                        ? 'bg-[#599559] border-[#767676]'
                        : 'bg-white border-[#767676]'
                        }`}
                    >
                      <div className={`flex items-center gap-2 mb-1 ${isUser ? 'text-white' : 'text-slate-700'
                        }`}>
                        <span className="font-semibold text-xs">
                          {msg.senderName || (isUser ? t('components.support:you') : t('components.support:support'))}
                        </span>
                      </div>
                      <div className={`text-xs whitespace-pre-wrap pb-4 ${isUser ? 'text-white' : 'text-slate-900'
                        }`}>
                        {msg.message}
                      </div>
                      <div className={`absolute bottom-1 right-2 text-[10px] leading-none ${isUser ? 'text-white/80' : 'text-slate-500'
                        }`}>
                        {formatTimeShort(msg.createdAt)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area - fixed at bottom */}
        <div className="p-3 border-t border-slate-200 flex-shrink-0">
          <form onSubmit={handleChatSubmit} className="flex gap-2">
            <input
              type="text"
              placeholder={t('components.support:messagePlaceholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="flex-1 h-8 px-4 text-xs bg-white border border-black rounded-full shadow-none focus:outline-none focus:border-black"
            />
            <button
              type="submit"
              disabled={createMessageMutation.isPending || !message.trim()}
              className="h-8 w-8 p-0 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-70 transition-opacity"
            >
              <img src={webIconSendSvg} alt="Send" className="h-8 w-8" />
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Original Dialog Layout
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-black">
            <MessageCircle className="h-5 w-5" />
            {t('components.support:header')}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-4 h-[600px]">
          {/* Nachrichtenliste - Threaded */}
          <div className="flex-1 border-r pr-4">
            <h3 className="text-sm font-medium mb-3 text-black">{t('components.support:listHeader')}</h3>
            <div className="space-y-2 overflow-y-auto h-[500px]">
              {isLoading ? (
                <div className="text-sm text-gray-500">{t('components.support:loading')}</div>
              ) : threads.length === 0 ? (
                <div className="text-sm text-gray-500">{t('components.support:empty')}</div>
              ) : (
                threads.map((thread: ThreadedMessage) => {
                  const hasUnreadReply = thread.replies.some((r: SupportMessage) => !r.isFromUser && !r.isRead);
                  return (
                    <div
                      key={thread.original.id}
                      className={`p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${hasUnreadReply ? 'bg-blue-50 border-blue-300' : 'border-gray-300'
                        }`}
                      onClick={() => {
                        setSelectedMessage(thread.original);
                        setShowMessageView(true);
                        // Mark unread replies as read
                        thread.replies.forEach((r: SupportMessage) => {
                          if (!r.isRead && !r.isFromUser) {
                            markAsReadMutation.mutate(r.id);
                          }
                        });
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs text-gray-500">
                          {thread.original.ticketId && (
                            <span className="font-mono text-blue-600">{thread.original.ticketId}</span>
                          )}
                          {thread.replies.length > 0 && (
                            <>
                              <span className="mx-1">•</span>
                              <span>{thread.replies.length} {t('components.support:replies', 'replies')}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {hasUnreadReply && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      <h4 className="text-sm font-medium truncate">{thread.original.subject}</h4>
                      <p className="text-xs text-gray-600 line-clamp-2 mt-1">{thread.original.message}</p>
                      <div className="text-xs text-gray-400 mt-2">
                        {thread.original.createdAt
                          ? formatDateTime(thread.original.createdAt)
                          : t('components.support:unknown')}
                      </div>
                      {/* Show last reply preview if exists */}
                      {thread.replies.length > 0 && (
                        <div className={`mt-2 p-2 rounded border ${thread.replies[thread.replies.length - 1].isFromUser
                          ? 'bg-gray-100 border-gray-200'
                          : 'bg-blue-50 border-blue-200'
                          }`}>
                          <div className="flex items-center gap-1 mb-1">
                            {thread.replies[thread.replies.length - 1].isFromUser ? (
                              <User className="w-3 h-3 text-gray-500" />
                            ) : (
                              <Shield className="w-3 h-3 text-blue-500" />
                            )}
                            <span className="text-xs text-gray-600 font-medium">
                              {thread.replies[thread.replies.length - 1].senderName || (
                                thread.replies[thread.replies.length - 1].isFromUser
                                  ? t('components.support:you')
                                  : t('components.support:support')
                              )}
                            </span>
                          </div>
                          <div className="text-xs text-gray-800 line-clamp-2">
                            {thread.replies[thread.replies.length - 1].message}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Neue Nachricht */}
          <div className="flex-1 pl-4">
            <h3 className="text-sm font-medium mb-3 text-black">{t('components.support:newMessage')}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-medium text-black mb-1 block">
                  {t('components.support:subject')}
                </label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('components.support:subjectPlaceholder')}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-black mb-1 block">
                  {t('components.support:message')}
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('components.support:messagePlaceholder')}
                  rows={8}
                  className="text-xs resize-none"
                />
              </div>
              <Button
                type="submit"
                disabled={createMessageMutation.isPending}
                className="w-full h-7 text-xs bg-black hover:bg-gray-800"
              >
                {createMessageMutation.isPending ? (
                  t('components.support:sending')
                ) : (
                  <>
                    <img src={webIconSendSvg} alt="Send" className="w-4 h-4 mr-2 invert" />
                    {t('components.support:send')}
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
